/**
 * LINKO Shop 智能選購 / 智能報價
 *
 * @typedef {'living'|'master_bedroom'|'bedroom'|'kitchen'|'bathroom'|'corridor'|'balcony'|'store'|'study'} RoomType
 * @typedef {'switch_1g'|'switch_2g'|'switch_3g'|'switch_2way'|'dimmer'|'socket_13a_1g'|'socket_13a_1g_sw'|'socket_13a_2g'|'socket_13a_2g_sw'|'socket_13a_2g_usb'|'socket_20a_dp'|'socket_32a_dp'|'socket_45a_dp'|'tv_outlet'|'data_outlet'|'phone_outlet'|'doorbell_button'|'curtain_switch'|'pir_sensor'|'blank_plate'|'smart_gateway'|'smart_lock'|'smart_curtain_motor'|'smart_ac_controller'|'ceiling_speaker'|'soundbar'|'karaoke'|'ev_charger'|'ev_install'|'router'|'camera'|'install_service'} Role
 * @typedef {Object} Answers
 * @property {number} areaSqft
 * @property {'public'|'private'|'village'|'tong'|'commercial'} propertyType
 * @property {Record<RoomType, number>} rooms
 * @property {Role[]} needs
 * @property {string} series
 * @property {'WE'|'WG'|'DG'|'BK'} colour
 * @property {'economy'|'standard'|'premium'} tier
 * @property {boolean} smartMode
 * @typedef {Object} QuoteLine
 * @property {Role} role
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {number} variantId
 * @property {string} variantTitle
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {number} qty
 * @property {boolean} available
 * @property {string} group
 * @property {string} reason
 * @property {'rule'|'sales'|'blend'|'manual'} source
 * @property {boolean} optional
 */

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function addQty(map, role, qty) {
  const n = Number(qty);
  if (!role || !n) return;
  map[role] = (map[role] || 0) + n;
}

function sumRooms(rooms) {
  let total = 0;
  Object.keys(rooms || {}).forEach(function (key) {
    total += Number(rooms[key]) || 0;
  });
  return total;
}

function accumulateBaseline(rooms, baseline) {
  const raw = {};
  Object.keys(rooms || {}).forEach(function (room) {
    const count = Number(rooms[room]) || 0;
    if (!count) return;
    const row = (baseline && baseline[room]) || {};
    Object.keys(row).forEach(function (role) {
      addQty(raw, role, row[role] * count);
    });
  });
  return raw;
}

function applyAreaFactor(rawQty, areaSqft, cfg, totalRooms) {
  const divisor = cfg.divisorBase + cfg.divisorPerRoom * totalRooms;
  const areaFactor = clamp((Number(areaSqft) || 0) / Math.max(divisor, 1), cfg.min, cfg.max);
  const qty = {};
  Object.keys(rawQty).forEach(function (role) {
    qty[role] = rawQty[role] * areaFactor;
  });
  return { qty: qty, areaFactor: areaFactor, divisor: divisor };
}

function applySmartUpgrade(rawQty, smartMode, map, opts) {
  const options = opts || {};
  const out = {};
  Object.keys(rawQty).forEach(function (role) {
    if (!smartMode || role === 'switch_2way' || !map || !map[role] || role === 'note') {
      addQty(out, role, rawQty[role]);
      return;
    }
    if (options.skipSwitchUpgrade && role.indexOf('switch_') === 0) {
      addQty(out, role, rawQty[role]);
      return;
    }
    addQty(out, map[role], rawQty[role]);
  });
  return out;
}

function evalNeedExpr(expr, rooms) {
  const src = String(expr || '').replace(/^=/, '').trim();
  let i = 0;
  function peek() {
    return src.charAt(i);
  }
  function eat() {
    const ch = src.charAt(i);
    i += 1;
    return ch;
  }
  function skip() {
    while (/\s/.test(peek())) eat();
  }
  function parsePrimary() {
    skip();
    if (src.slice(i, i + 4) === 'max(') {
      i += 4;
      const a = parseExpr();
      skip();
      if (peek() !== ',') throw new Error('expr');
      eat();
      const b = parseExpr();
      skip();
      if (peek() !== ')') throw new Error('expr');
      eat();
      return Math.max(a, b);
    }
    if (src.slice(i, i + 6) === 'rooms.') {
      i += 6;
      let key = '';
      while (/[a-z_]/.test(peek())) key += eat();
      return Number(rooms[key]) || 0;
    }
    if (peek() === '(') {
      eat();
      const v = parseExpr();
      skip();
      if (peek() !== ')') throw new Error('expr');
      eat();
      return v;
    }
    if (peek() === '-') {
      eat();
      return -parsePrimary();
    }
    let num = '';
    while (/\d/.test(peek())) num += eat();
    if (!num) throw new Error('expr');
    return Number(num);
  }
  function parseTerm() {
    let v = parsePrimary();
    skip();
    while (peek() === '*') {
      eat();
      v *= parsePrimary();
      skip();
    }
    return v;
  }
  function parseExpr() {
    let v = parseTerm();
    skip();
    while (peek() === '+' || peek() === '-') {
      const op = eat();
      const r = parseTerm();
      v = op === '+' ? v + r : v - r;
      skip();
    }
    return v;
  }
  const value = parseExpr();
  skip();
  if (i < src.length) throw new Error('expr leftover');
  return value;
}

function resolveNeedQty(value, rooms) {
  if (typeof value === 'number') return value;
  const s = String(value).trim();
  if (!s) return 0;
  if (s.charAt(0) === '=') {
    try {
      return evalNeedExpr(s, rooms);
    } catch (err) {
      return 0;
    }
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function applyWholeUnit(rawQty, answers, wholeUnit) {
  const out = Object.assign({}, rawQty);
  const always = (wholeUnit && wholeUnit.always) || {};
  Object.keys(always).forEach(function (role) {
    if (role === 'note') return;
    addQty(out, role, resolveNeedQty(always[role], answers.rooms));
  });
  if (answers.smartMode) {
    const smart = (wholeUnit && wholeUnit.smartMode) || {};
    Object.keys(smart).forEach(function (role) {
      if (role === 'note') return;
      addQty(out, role, resolveNeedQty(smart[role], answers.rooms));
    });
  }
  const byNeed = (wholeUnit && wholeUnit.byNeed) || {};
  (answers.needs || []).forEach(function (need) {
    const row = byNeed[need];
    if (!row) return;
    Object.keys(row).forEach(function (role) {
      addQty(out, role, resolveNeedQty(row[role], answers.rooms));
    });
  });
  return out;
}

function applyTier(rawQty, tier, multipliers) {
  const m = (multipliers && multipliers[tier]) || 1;
  const out = {};
  Object.keys(rawQty).forEach(function (role) {
    out[role] = rawQty[role] * m;
  });
  return out;
}

function blendWithSales(rawQty, insights) {
  const roles = (insights && insights.roles) || {};
  const qty = {};
  const source = {};
  const blendedRoles = [];
  Object.keys(rawQty).forEach(function (role) {
    let value = rawQty[role];
    let src = 'rule';
    const s = roles[role];
    if (s && s.orders >= 5) {
      const blended = value * 0.7 + s.medianQty * 0.3;
      if (Math.abs(blended - value) / Math.max(value, 1) > 0.05) {
        src = 'blend';
        blendedRoles.push(role);
      }
      value = blended;
    }
    qty[role] = Math.max(0, Math.round(value));
    source[role] = src;
  });
  return { qty: qty, source: source, blendedRoles: blendedRoles };
}

function colourMatches(variant, colourId, colours) {
  if (!variant || !colourId) return false;
  if (variant.colour === colourId) return true;
  const sku = String(variant.sku || '').toUpperCase();
  if (sku.indexOf('_' + colourId) !== -1) return true;
  const blob = [variant.title].concat(
    (variant.selectedOptions || []).map(function (o) { return o.value; })
  ).join(' ');
  if (colourId === 'WE' && /^(白|小米白|白色|搪瓷白)$/.test(String(variant.title || '').trim())) return true;
  if (colourId === 'DG' && /深沙銀|石墨灰/.test(blob)) return true;
  if (colourId === 'WG' && /香檳金|沉醉金|玫瑰金/.test(blob)) return true;
  const row = (colours || []).filter(function (c) { return c.id === colourId; })[0];
  if (!row) return blob.indexOf(colourId) !== -1;
  return (row.match || []).some(function (m) { return blob.indexOf(m) !== -1; });
}

function groupForRole(role, groups) {
  const keys = Object.keys(groups || {});
  for (let i = 0; i < keys.length; i += 1) {
    if ((groups[keys[i]] || []).indexOf(role) !== -1) return keys[i];
  }
  return '其他';
}

function tertileScore(price, prices, tier) {
  if (!prices.length) return 0;
  const i1 = prices[Math.floor(prices.length / 3)];
  const i2 = prices[Math.floor((prices.length * 2) / 3)];
  if (tier === 'economy') return price <= i1 ? 0 : 1;
  if (tier === 'premium') return price >= i2 ? 0 : 1;
  return price >= i1 && price <= i2 ? 0 : 1;
}

const ROLE_ALIASES = {
  switch_1g: ['switch_1g', 'switch_2g', 'switch_3g', 'switch_2way'],
  switch_2g: ['switch_2g', 'switch_1g', 'switch_3g', 'switch_2way'],
  switch_3g: ['switch_3g', 'switch_1g', 'switch_2g', 'switch_2way'],
  dimmer: ['dimmer', 'switch_1g', 'switch_2way'],
  socket_13a_1g: ['socket_13a_1g', 'socket_13a_1g_sw', 'socket_13a_2g_usb'],
  socket_13a_1g_sw: ['socket_13a_1g_sw', 'socket_13a_1g', 'socket_13a_2g_usb'],
  socket_13a_2g: ['socket_13a_2g', 'socket_13a_2g_sw', 'socket_13a_2g_usb'],
  socket_13a_2g_sw: ['socket_13a_2g_sw', 'socket_13a_2g', 'socket_13a_2g_usb'],
};

const SERIES_FAMILIES = [
  ['avataron', 'avataron_c'],
  ['galion', 'mallia', 'netatmo'],
  ['seata', 'arina'],
];

function seriesFamily(id) {
  for (let i = 0; i < SERIES_FAMILIES.length; i += 1) {
    if (SERIES_FAMILIES[i].indexOf(id) !== -1) return SERIES_FAMILIES[i];
  }
  return id ? [id] : [];
}

function inferSeries(product, config) {
  const raw = String(product && product.series || '').trim();
  const series = (config && config.series) || [];
  if (raw && series.some(function (s) { return s.id === raw; })) return raw;
  const t = [
    product && product.series,
    product && product.title,
    product && product.handle,
  ].filter(Boolean).join(' ').toLowerCase();
  if (/avataron.?c|奐然/.test(t)) return 'avataron_c';
  if (/avataron|奐尚/.test(t)) return 'avataron';
  if (/netatmo/.test(t)) return 'netatmo';
  if (/mallia|senses/.test(t)) return 'mallia';
  if (/galion|逸景|legrand|羅格朗/.test(t)) return 'galion';
  if (/seata/.test(t)) return 'seata';
  if (/arina/.test(t)) return 'arina';
  if (/eubiq/.test(t)) return 'eubiq';
  if (/aqara/.test(t)) return 'aqara';
  if (/yeelight/.test(t)) return 'yeelight';
  if (/lifesmart/.test(t)) return 'lifesmart';
  if (/sonoff|tuya/.test(t)) return 'sonoff';
  return raw || '';
}

function seriesMatches(product, wantSeries, config) {
  if (!wantSeries) return true;
  if (inferSeries(product, config) === wantSeries) return true;
  const row = ((config && config.series) || []).filter(function (s) { return s.id === wantSeries; })[0];
  const keys = (row && row.match) || [];
  const hay = [product && product.title, product && product.handle].filter(Boolean).join(' ').toLowerCase();
  return keys.some(function (k) {
    return hay.indexOf(String(k).toLowerCase()) >= 0;
  });
}

function seriesByKind(config, smart) {
  return ((config && config.series) || []).filter(function (s) {
    return smart ? s.kind === 'smart' : s.kind !== 'smart';
  });
}

function seriesRowOf(config, id) {
  return ((config && config.series) || []).filter(function (s) { return s.id === id; })[0];
}

function syncColourToSeries(answers, row) {
  const colours = (row && row.colours) || [];
  if (colours.length && colours.indexOf(answers.colour) === -1) {
    answers.colour = colours[0];
  }
}

function catalogProducts(role, catalog) {
  const keys = ROLE_ALIASES[role] || [role];
  const out = [];
  const seen = {};
  keys.forEach(function (key) {
    ((catalog && catalog.roles && catalog.roles[key]) || []).forEach(function (p) {
      if (seen[p.handle]) return;
      seen[p.handle] = true;
      out.push(p);
    });
  });
  return out;
}

function productInRole(product, role, catalog) {
  return ((catalog && catalog.roles && catalog.roles[role]) || []).some(function (p) {
    return p.handle === product.handle;
  });
}

function gangFromBlob(blob) {
  const text = String(blob || '');
  if (/(四|4)\s*位/.test(text)) return 4;
  if (/(三|3)\s*位/.test(text)) return 3;
  if (/(兩|两|二|2)\s*位/.test(text)) return 2;
  if (/(單|一|1)\s*位/.test(text)) return 1;
  return 0;
}

function variantGangOf(product, variant) {
  if (variant && variant.gang) return variant.gang;
  return gangFromBlob([
    variant && variant.title,
    product && product.title,
    product && product.handle,
  ].join(' '));
}

function pickVariant(role, answers, catalog, insights, config) {
  const list = catalogProducts(role, catalog);
  if (!list.length) return null;
  const wantSeries = answers && answers.series;
  const family = seriesFamily(wantSeries);
  let pool = list.filter(function (p) { return seriesMatches(p, wantSeries, config); });
  let fallback = false;
  if (!pool.length && family.length) {
    pool = list.filter(function (p) {
      return family.indexOf(inferSeries(p, config)) !== -1;
    });
    fallback = pool.length > 0;
  }
  if (!pool.length) return null;
  const cands = [];
  pool.forEach(function (product) {
    (product.variants || []).forEach(function (variant) {
      cands.push({ product: product, variant: variant });
    });
  });
  if (!cands.length) return null;
  const wantGang = { switch_1g: 1, switch_2g: 2, switch_3g: 3, socket_13a_1g: 1, socket_13a_1g_sw: 1, socket_13a_2g: 2, socket_13a_2g_sw: 2 }[role];
  const top = ((insights && insights.topVariantsByRole) || {})[role] || [];
  const rank = {};
  top.forEach(function (row, idx) { rank[row.variantId] = idx; });
  const prices = cands.map(function (c) { return c.variant.price || 0; }).filter(function (p) { return p > 0; }).sort(function (a, b) { return a - b; });
  cands.sort(function (a, b) {
    const ea = productInRole(a.product, role, catalog) ? 0 : 1;
    const eb = productInRole(b.product, role, catalog) ? 0 : 1;
    if (ea !== eb) return ea - eb;
    if (wantGang) {
      const ga = variantGangOf(a.product, a.variant) === wantGang ? 0 : 1;
      const gb = variantGangOf(b.product, b.variant) === wantGang ? 0 : 1;
      if (ga !== gb) return ga - gb;
    }
    const ca = colourMatches(a.variant, answers.colour, config.colours) ? 0 : 1;
    const cb = colourMatches(b.variant, answers.colour, config.colours) ? 0 : 1;
    if (ca !== cb) return ca - cb;
    const aa = a.variant.available === false ? 1 : 0;
    const ab = b.variant.available === false ? 1 : 0;
    if (aa !== ab) return aa - ab;
    const ra = rank[a.variant.id] != null ? rank[a.variant.id] : 99;
    const rb = rank[b.variant.id] != null ? rank[b.variant.id] : 99;
    if (ra !== rb) return ra - rb;
    return tertileScore(a.variant.price || 0, prices, answers.tier) - tertileScore(b.variant.price || 0, prices, answers.tier);
  });
  const chosen = cands[0];
  const chosenSeries = inferSeries(chosen.product, config);
  fallback = fallback || (!!wantSeries && chosenSeries && chosenSeries !== wantSeries);
  return { pick: chosen, fallback: fallback, product: chosen.product };
}

function toLine(role, product, variant, qty, reason, source, group, optional) {
  return {
    role: role,
    handle: product.handle,
    title: product.title,
    image: product.image || '',
    variantId: variant.id,
    variantTitle: variant.title || '',
    price: variant.price || 0,
    compareAtPrice: variant.compareAtPrice || 0,
    qty: qty,
    available: variant.available !== false,
    group: group,
    reason: reason,
    source: source,
    optional: !!optional,
  };
}

function consolidateGangs(role, qty, answers, catalog, insights, config, reason, source) {
  const picked = pickVariant(role, answers, catalog, insights, config);
  if (!picked) return [];
  const group = groupForRole(role, config.groups);
  const family = seriesFamily(answers && answers.series);
  const products = catalogProducts(role, catalog).filter(function (p) {
    return seriesMatches(p, answers.series, config) || family.indexOf(inferSeries(p, config)) !== -1;
  });
  function findGang(gang) {
    const pool = products.length ? products : [picked.pick.product];
    for (let i = 0; i < pool.length; i += 1) {
      const product = pool[i];
      const hit = (product.variants || []).filter(function (v) {
        return variantGangOf(product, v) === gang && colourMatches(v, answers.colour, config.colours);
      })[0] || (product.variants || []).filter(function (v) {
        return variantGangOf(product, v) === gang;
      })[0];
      if (hit) return { product: product, variant: hit };
    }
    return null;
  }
  if (role !== 'switch_1g' || qty < 2) {
    return [toLine(role, picked.pick.product, picked.pick.variant, qty, reason, source, group, false)];
  }
  const lines = [];
  let gangs = qty;
  const g3 = findGang(3);
  const g2 = findGang(2);
  const g1 = findGang(1) || { product: picked.pick.product, variant: picked.pick.variant };
  while (gangs >= 3 && g3) {
    lines.push(toLine(role, g3.product, g3.variant, 1, reason + ' · 已合併為 3 位掣，減少開孔', source, group, false));
    gangs -= 3;
  }
  while (gangs >= 2 && g2) {
    lines.push(toLine(role, g2.product, g2.variant, 1, reason + ' · 已合併為 2 位掣，減少開孔', source, group, false));
    gangs -= 2;
  }
  if (gangs > 0) {
    lines.push(toLine(role, g1.product, g1.variant, gangs, reason, source, group, false));
  }
  return lines;
}

function buildAddons(lines, insights, answers, catalog, config, limit) {
  const have = {};
  lines.forEach(function (line) { have[line.role] = true; });
  const affinity = (insights && insights.affinity) || [];
  const scored = {};
  affinity.forEach(function (row) {
    if (!have[row.a] || have[row.b] || row.confidence < 0.35) return;
    const prev = scored[row.b];
    if (!prev || row.lift > prev.lift) scored[row.b] = row;
  });
  return Object.keys(scored)
    .map(function (role) { return scored[role]; })
    .sort(function (a, b) { return b.lift - a.lift; })
    .slice(0, limit)
    .map(function (row) {
      const picked = pickVariant(row.b, answers, catalog, insights, config);
      if (!picked) return null;
      const pct = Math.round(row.confidence * 100);
      return toLine(
        row.b,
        picked.pick.product,
        picked.pick.variant,
        0,
        '有 ' + pct + '% 買咗同類產品嘅客戶一齊選購',
        'sales',
        groupForRole(row.b, config.groups),
        true
      );
    })
    .filter(Boolean);
}

function roomSummary(rooms, labels) {
  const parts = [];
  Object.keys(rooms || {}).forEach(function (key) {
    const n = Number(rooms[key]) || 0;
    if (n > 0) parts.push((labels && labels[key] ? labels[key] : key) + n);
  });
  return parts.join(' · ');
}

const ROLE_USAGE = {
  switch_1g: '單控燈掣',
  switch_2g: '兩位燈掣',
  switch_3g: '三位燈掣',
  switch_2way: '兩地控制燈掣',
  dimmer: '調光掣',
  socket_13a_1g: '13A 單位插座',
  socket_13a_1g_sw: '13A 單位掣插座',
  socket_13a_2g: '13A 雙位插座',
  socket_13a_2g_sw: '13A 雙位掣插座',
  socket_13a_2g_usb: 'USB 充電位',
  socket_20a_dp: '20A 熱水爐／暖爐掣',
  socket_32a_dp: '32A 煮食爐掣',
  socket_45a_dp: '45A 煮食爐掣',
  tv_outlet: '電視天線位',
  data_outlet: '網絡位',
  phone_outlet: '電話線位',
  doorbell_button: '門鐘掣',
  curtain_switch: '窗簾掣',
  pir_sensor: '感應燈',
  blank_plate: '預留空位蓋板',
  smart_gateway: '智能網關，成屋共用',
  smart_lock: '大門智能門鎖',
  smart_curtain_motor: '窗簾馬達',
  smart_ac_controller: '冷氣智能控制',
  ceiling_speaker: '天花喇叭',
  soundbar: '客廳 Soundbar',
  karaoke: '卡拉OK',
  ev_charger: '電車充電器',
  ev_install: 'EV 充電安裝',
  router: '網絡升級',
  camera: '安防攝影機',
  install_service: '建議由 LINKO 持牌技工安裝',
};

const ROLE_HINT_ROOMS = {
  doorbell_button: ['corridor'],
  tv_outlet: ['living'],
  soundbar: ['living'],
  karaoke: ['living'],
  ceiling_speaker: ['living'],
  smart_curtain_motor: ['living', 'master_bedroom'],
  smart_ac_controller: ['living', 'master_bedroom', 'bedroom'],
  smart_lock: ['corridor'],
  ev_charger: [],
};

function shortRoomLabel(key, labels) {
  const raw = (labels && labels[key]) || key;
  return String(raw).split(/\s*\/\s*/)[0];
}

function roomsUsingRole(role, rooms, config) {
  const labels = config && config.roomLabels;
  const baseline = config && config.baseline;
  const seen = {};
  const parts = [];
  function add(key) {
    const n = Number(rooms && rooms[key]) || 0;
    if (!n || seen[key]) return;
    seen[key] = true;
    parts.push(n > 1 ? shortRoomLabel(key, labels) + n : shortRoomLabel(key, labels));
  }
  Object.keys(rooms || {}).forEach(function (key) {
    const row = (baseline && baseline[key]) || {};
    if (row[role]) add(key);
  });
  if (!parts.length) {
    (ROLE_HINT_ROOMS[role] || []).forEach(add);
  }
  return parts;
}

function reasonForRole(role, answers, config, extras) {
  const extra = extras || {};
  const usage = ROLE_USAGE[role] || '';
  const rooms = roomsUsingRole(role, answers && answers.rooms, config);
  let reason = rooms.length && usage
    ? rooms.join('、') + ' · ' + usage
    : (usage || rooms.join('、') || '單位配置');
  if (extra.blend) reason += ' · 已按訂單用量微調';
  if (extra.fallbackLabel) reason += ' · 此系列未有此型號，已改用同品牌 ' + extra.fallbackLabel;
  if (extra.merged === 3) reason += ' · 已合併為 3 位掣，減少開孔';
  if (extra.merged === 2) reason += ' · 已合併為 2 位掣，減少開孔';
  return reason;
}

/**
 * @param {Answers} answers
 * @param {object} config
 * @param {object} catalog
 * @param {object} insights
 * @param {{addonLimit?: number, autoAddInstall?: boolean}} [opts]
 */
function recommendEngine(answers, config, catalog, insights, opts) {
  const options = opts || {};
  const totalRooms = sumRooms(answers.rooms);
  const rawA = accumulateBaseline(answers.rooms, config.baseline);
  const area = applyAreaFactor(rawA, answers.areaSqft, config.areaFactor, totalRooms);
  const seriesRow = seriesRowOf(config, answers.series);
  const upgraded = applySmartUpgrade(area.qty, answers.smartMode, config.smartUpgradeMap, {
    skipSwitchUpgrade: !!(seriesRow && seriesRow.kind === 'smart'),
  });
  const withUnit = applyWholeUnit(upgraded, answers, config.wholeUnit);
  const tiered = applyTier(withUnit, answers.tier, config.tierMultiplier);
  const blended = blendWithSales(tiered, insights || {});
  const fallbackRoles = [];
  const missingRoles = [];
  const lines = [];
  Object.keys(blended.qty).forEach(function (role) {
    const qty = blended.qty[role];
    if (!qty) return;
    const picked = pickVariant(role, answers, catalog || {}, insights || {}, config);
    if (!picked) {
      missingRoles.push(role);
      return;
    }
    if (picked.fallback) fallbackRoles.push(role);
    const usedSeries = inferSeries(picked.pick.product, config);
    let reason = reasonForRole(role, answers, config, {
      blend: blended.source[role] === 'blend',
      fallback: false,
    });
    if (picked.fallback && usedSeries && usedSeries !== answers.series) {
      reason += ' · 此系列未有此型號，已改用同品牌 ' + seriesLabel(config, usedSeries);
    }
    consolidateGangs(role, qty, answers, catalog || {}, insights || {}, config, reason, blended.source[role]).forEach(function (line) {
      lines.push(line);
    });
  });
  const addons = buildAddons(lines, insights || {}, answers, catalog || {}, config, options.addonLimit == null ? 6 : options.addonLimit);
  let totalPoints = 0;
  lines.forEach(function (line) { totalPoints += line.qty; });
  const needs = answers.needs || [];
  const wantInstall = options.autoAddInstall !== false && (totalPoints >= 15 || needs.indexOf('ev_charger') !== -1 || needs.indexOf('smart_lock') !== -1);
  if (wantInstall && !lines.some(function (l) { return l.role === 'install_service'; })) {
    const picked = pickVariant('install_service', answers, catalog || {}, insights || {}, config);
    if (picked) {
      addons.unshift(toLine(
        'install_service',
        picked.pick.product,
        picked.pick.variant,
        0,
        '建議由 LINKO 持牌技工安裝',
        'rule',
        groupForRole('install_service', config.groups),
        true
      ));
    }
  }
  return {
    lines: lines,
    addons: addons,
    meta: {
      areaFactor: area.areaFactor,
      totalRooms: totalRooms,
      totalPoints: totalPoints,
      insightsGeneratedAt: insights && insights.generatedAt,
      insightsOrderCount: insights && insights.orderCount,
      windowActualDays: insights && insights.windowActualDays,
      blendedRoles: blended.blendedRoles,
      fallbackRoles: fallbackRoles,
      missingRoles: missingRoles,
    },
  };
}

const ROOM_KEYS = ['living', 'master_bedroom', 'bedroom', 'kitchen', 'bathroom', 'corridor', 'balcony', 'store', 'study'];
const NEED_CHIPS = [
  { id: 'dimmer', label: '燈光調光' },
  { id: 'smart_curtain_motor', label: '智能窗簾' },
  { id: 'smart_lock', label: '智能門鎖' },
  { id: 'smart_ac_controller', label: '冷氣控制' },
  { id: 'ceiling_speaker', label: '天花喇叭' },
  { id: 'soundbar', label: 'Soundbar' },
  { id: 'karaoke', label: '卡拉OK' },
  { id: 'router', label: '網絡升級' },
  { id: 'ev_charger', label: 'EV 充電' },
  { id: 'camera', label: '安防攝影機' },
];
const PROPERTY_TYPES = [
  { id: 'public', label: '公屋' },
  { id: 'private', label: '私樓' },
  { id: 'village', label: '村屋' },
  { id: 'tong', label: '唐樓' },
  { id: 'commercial', label: '商舖 / 辦公室' },
];
const TIERS = [
  { id: 'economy', label: '經濟', text: '夠用為主，優先選走量型號。' },
  { id: 'standard', label: '標準', text: '香港裝修最常見配置。' },
  { id: 'premium', label: '尊尚', text: '較齊全、較高階型號。' },
];

function defaultAnswers() {
  return {
    areaSqft: 600,
    propertyType: 'private',
    rooms: {
      living: 1,
      master_bedroom: 0,
      bedroom: 1,
      kitchen: 1,
      bathroom: 1,
      corridor: 1,
      balcony: 0,
      store: 0,
      study: 0,
    },
    needs: [],
    series: 'avataron',
    colour: 'WE',
    tier: 'standard',
    smartMode: false,
  };
}

class QuoteState extends EventTarget {
  constructor() {
    super();
    this.step = 1;
    this.view = 'wizard';
    this.answers = defaultAnswers();
    this.lines = [];
    this.addons = [];
    this.meta = {};
    this.toast = '';
    this.undo = null;
    this.shareBanner = false;
    this.error = '';
    this.busy = false;
    this.modal = null;
  }
  emit() {
    this.dispatchEvent(new Event('change'));
  }
}

function encodeQuote(answers, lines) {
  const a = answers || {};
  const rooms = a.rooms || {};
  const payload = {
    v: 2,
    s: a.areaSqft,
    p: a.propertyType,
    r: ROOM_KEYS.map(function (key) { return Number(rooms[key]) || 0; }),
    n: a.needs || [],
    se: a.series,
    c: a.colour,
    t: a.tier,
    m: a.smartMode ? 1 : 0,
    l: (lines || []).filter(function (x) { return x && x.qty > 0; }).map(function (x) {
      return [x.variantId, x.qty];
    }),
  };
  return btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function sanitizeShareToken(q) {
  return String(q || '').trim().replace(/\s+/g, '').replace(/[.,;!?）)]+$/g, '');
}

function persistShareToken(token) {
  try {
    const clean = sanitizeShareToken(token);
    if (clean) sessionStorage.setItem('linko_sq_share', clean);
  } catch (err) {}
}

function restoreShareToken() {
  try {
    return sanitizeShareToken(sessionStorage.getItem('linko_sq_share') || '');
  } catch (err) {
    return '';
  }
}

function readShareToken() {
  try {
    if (typeof window !== 'undefined' && window.LINKO_SQ_SHARE) {
      const fromWin = sanitizeShareToken(window.LINKO_SQ_SHARE);
      if (fromWin) return fromWin;
    }
  } catch (err0) {}
  try {
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    const fromUrl = sanitizeShareToken(params.get('sq') || params.get('q') || '');
    if (fromUrl) return fromUrl;
  } catch (err) {}
  try {
    const hash = typeof location !== 'undefined' ? String(location.hash || '') : '';
    const hm = hash.match(/(?:^#|[?&])(?:sq|q)=([^&]+)/);
    if (hm) return sanitizeShareToken(decodeURIComponent(hm[1]));
  } catch (err2) {}
  if (typeof document !== 'undefined') {
    const node = document.getElementById('linko-sq-share');
    if (node) {
      try {
        const data = JSON.parse(node.textContent);
        const fromNode = sanitizeShareToken((data && (data.sq || data.q)) || '');
        if (fromNode) return fromNode;
      } catch (err3) {}
    }
  }
  const hash = typeof location !== 'undefined' ? String(location.hash || '') : '';
  if (hash.indexOf('#quote') === 0) {
    const stored = restoreShareToken();
    if (stored) return stored;
  }
  return '';
}

function decodeQuote(q) {
  const token = sanitizeShareToken(q);
  const pad = token.length % 4 === 0 ? '' : '===='.slice(token.length % 4);
  const raw = token.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const parsed = JSON.parse(decodeURIComponent(escape(atob(raw))));
  if (parsed && parsed.v === 2) {
    const rooms = {};
    ROOM_KEYS.forEach(function (key, i) {
      rooms[key] = Number((parsed.r || [])[i]) || 0;
    });
    return {
      v: 2,
      a: {
        areaSqft: Number(parsed.s) || 600,
        propertyType: parsed.p || 'private',
        rooms: rooms,
        needs: parsed.n || [],
        series: parsed.se || 'avataron',
        colour: parsed.c || 'WE',
        tier: parsed.t || 'standard',
        smartMode: !!parsed.m,
      },
      l: parsed.l || [],
    };
  }
  return parsed;
}

function findVariantInCatalog(catalog, variantId, handle, role) {
  const wantId = Number(variantId);
  const roles = (catalog && catalog.roles) || {};
  const keys = Object.keys(roles);
  if (role && keys.indexOf(role) !== -1) {
    keys.splice(keys.indexOf(role), 1);
    keys.unshift(role);
  }
  let handleHit = null;
  for (let i = 0; i < keys.length; i += 1) {
    const list = roles[keys[i]] || [];
    for (let j = 0; j < list.length; j += 1) {
      const product = list[j];
      const variants = product.variants || [];
      for (let k = 0; k < variants.length; k += 1) {
        if (Number(variants[k].id) === wantId) {
          return { product: product, variant: variants[k], role: keys[i] };
        }
      }
      if (handle && product.handle === handle && !handleHit && variants[0]) {
        handleHit = { product: product, variant: variants[0], role: keys[i] };
      }
    }
  }
  return handleHit;
}

function linesFromShare(decoded, catalog, config) {
  const out = [];
  const seen = {};
  (decoded.l || []).forEach(function (pair) {
    const vid = pair[0];
    const qty = Number(pair[1]) || 0;
    const handle = pair[2] || '';
    const role = pair[3] || '';
    if (!qty) return;
    const found = findVariantInCatalog(catalog, vid, handle, role);
    if (!found) return;
    const key = found.variant.id;
    if (seen[key]) {
      seen[key].qty += qty;
      return;
    }
    const lineRole = role || found.role;
    const line = toLine(
      lineRole,
      found.product,
      found.variant,
      qty,
      reasonForRole(lineRole, decoded.a || {}, config, {}),
      'manual',
      groupForRole(lineRole, config && config.groups),
      false
    );
    seen[key] = line;
    out.push(line);
  });
  return out;
}

function applySharedQuote(state, decoded, catalog, config, insights, settings) {
  if (!state || !decoded || !decoded.a) return false;
  const opts = settings || {};
  state.answers = Object.assign(defaultAnswers(), decoded.a);
  try {
    const restored = linesFromShare(decoded, catalog, config);
    if (restored.length) {
      state.lines = restored;
      state.addons = [];
      state.meta = { totalPoints: restored.reduce(function (n, line) { return n + line.qty; }, 0) };
    } else {
      const rebuilt = recommendEngine(state.answers, config, catalog, insights || {}, {
        addonLimit: opts.addonLimit,
        autoAddInstall: opts.autoInstall,
      });
      state.lines = rebuilt.lines;
      state.addons = rebuilt.addons;
      state.meta = rebuilt.meta;
    }
  } catch (err) {
    if (!state.lines) state.lines = [];
    if (!state.addons) state.addons = [];
  }
  state.view = 'quote';
  state.shareBanner = true;
  return true;
}

function moneyHKD(cents) {
  return new Intl.NumberFormat('zh-HK', { style: 'currency', currency: 'HKD', maximumFractionDigits: 0 }).format((cents || 0) / 100);
}

function totalsOf(lines) {
  let sub = 0;
  let compare = 0;
  let qty = 0;
  (lines || []).forEach(function (line) {
    if (line.qty <= 0) return;
    sub += line.price * line.qty;
    compare += (line.compareAtPrice || line.price) * line.qty;
    qty += line.qty;
  });
  return { sub: sub, compare: compare, save: Math.max(0, compare - sub), grand: sub, qty: qty };
}

function seriesLabel(config, id) {
  const row = ((config && config.series) || []).filter(function (s) { return s.id === id; })[0];
  return row ? row.label : id;
}

function colourLabel(config, id) {
  const row = ((config && config.colours) || []).filter(function (c) { return c.id === id; })[0];
  return row ? row.label : id;
}

function buildWhatsAppText(answers, lines, totals, shareUrl, prefix, config) {
  const summary = roomSummary(answers.rooms, config.roomLabels);
  const rows = (lines || []).filter(function (l) { return l.qty > 0; });
  const qty = totals && totals.qty != null ? totals.qty : rows.reduce(function (n, l) { return n + (Number(l.qty) || 0); }, 0);
  return [
    prefix || '你好 LINKO，我喺網站用智能選購整咗份報價，想跟進：',
    '',
    '📐 ' + answers.areaSqft + ' 呎 · ' + summary,
    '🎨 ' + seriesLabel(config, answers.series) + ' · ' + colourLabel(config, answers.colour) + ' · ' + (answers.smartMode ? '智能燈制' : '普通燈制'),
    '📦 共 ' + rows.length + ' 項、' + qty + ' 件',
    '💰 合計 ' + moneyHKD(totals && totals.grand) + '（未計安裝）',
    '',
    '請撳下面連結睇完整報價：',
    shareUrl,
  ].join('\n');
}

const isBrowser = typeof document !== 'undefined';

function track() {
  const args = Array.prototype.slice.call(arguments);
  const name = args[0];
  const params = args[1] || {};
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, params));
  } catch (err) {}
  try {
    if (name === 'sq_generate' && typeof fbq === 'function') fbq('track', 'Lead', { value: params.value, currency: 'HKD' });
    if ((name === 'add_to_cart' || name === 'sq_add_to_cart') && typeof fbq === 'function') fbq('track', 'AddToCart', { value: params.value, currency: 'HKD' });
  } catch (err2) {}
}

function absUrl(url) {
  if (!url) return url;
  if (url.indexOf('//') === 0) return (typeof location !== 'undefined' && location.protocol ? location.protocol : 'https:') + url;
  return url;
}

function readInlineJson(id) {
  if (typeof document === 'undefined') return null;
  const node = document.getElementById(id);
  if (!node) return null;
  try { return JSON.parse(node.textContent); } catch (err) { return null; }
}

function fetchTimeout(url, ms) {
  const ctrl = new AbortController();
  const timer = setTimeout(function () { ctrl.abort(); }, ms || 8000);
  return fetch(absUrl(url), { signal: ctrl.signal, credentials: 'same-origin' }).then(function (res) {
    clearTimeout(timer);
    return res;
  }, function (err) {
    clearTimeout(timer);
    throw err;
  });
}

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise(function (resolve, reject) {
    const input = document.createElement('input');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(input);
    ok ? resolve() : reject(new Error('copy'));
  });
}

function findSection() {
  return document.querySelector('.linko-sq');
}

function readSettings(root) {
  return {
    configUrl: root.getAttribute('data-config-url'),
    catalogUrl: root.getAttribute('data-catalog-url'),
    insightsUrl: root.getAttribute('data-insights-url'),
    whatsapp: root.getAttribute('data-whatsapp-number') || '85294013777',
    prefix: root.getAttribute('data-whatsapp-prefix') || '你好 LINKO，我喺網站用智能選購整咗份報價，想跟進：',
    showSales: root.getAttribute('data-show-sales-note') !== 'false',
    autoInstall: root.getAttribute('data-auto-add-install') !== 'false',
    addonLimit: Number(root.getAttribute('data-addon-limit') || 6),
    cartBehavior: root.getAttribute('data-cart-behavior') || 'append',
  };
}

function persist(answers) {
  try { sessionStorage.setItem('linko_sq_answers', JSON.stringify(answers)); } catch (err) {}
}

function restoreAnswers() {
  try {
    const raw = sessionStorage.getItem('linko_sq_answers');
    if (!raw) return null;
    return Object.assign(defaultAnswers(), JSON.parse(raw));
  } catch (err) {
    return null;
  }
}

function productCacheGet(handle) {
  try {
    const raw = sessionStorage.getItem('linko_sq_p_' + handle);
    if (!raw) return null;
    const row = JSON.parse(raw);
    if (Date.now() - row.t > 5 * 60 * 1000) return null;
    return row.d;
  } catch (err) {
    return null;
  }
}

function productCacheSet(handle, data) {
  try { sessionStorage.setItem('linko_sq_p_' + handle, JSON.stringify({ t: Date.now(), d: data })); } catch (err) {}
}

async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let idx = 0;
  async function worker() {
    while (idx < items.length) {
      const cur = idx;
      idx += 1;
      out[cur] = await fn(items[cur], cur);
    }
  }
  const n = Math.min(limit, items.length) || 0;
  const jobs = [];
  for (let i = 0; i < n; i += 1) jobs.push(worker());
  await Promise.all(jobs);
  return out;
}

async function hydratePrices(lines) {
  const handles = [];
  (lines || []).forEach(function (line) {
    if (line.handle && handles.indexOf(line.handle) === -1) handles.push(line.handle);
  });
  const map = {};
  try {
    await mapLimit(handles, 6, async function (handle) {
      try {
        const cached = productCacheGet(handle);
        if (cached) {
          map[handle] = cached;
          return;
        }
        const path = '/products/' + encodeURIComponent(handle) + '.js';
        const res = await fetchTimeout(path, 8000);
        if (!res || !res.ok) return;
        const json = await res.json();
        map[handle] = json;
        productCacheSet(handle, json);
      } catch (err) {}
    });
  } catch (err2) {}
  (lines || []).forEach(function (line) {
    const product = map[line.handle];
    if (!product) return;
    const variant = (product.variants || []).filter(function (v) { return v.id === line.variantId; })[0];
    if (!variant) return;
    line.price = Number(variant.price) || line.price;
    line.compareAtPrice = Number(variant.compare_at_price) || line.compareAtPrice;
    line.available = variant.available !== false;
    if (product.featured_image && !line.image) line.image = product.featured_image;
  });
}

function el(html) {
  const wrap = document.createElement('div');
  wrap.innerHTML = html.trim();
  return wrap.firstElementChild;
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, function (ch) {
    return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch];
  });
}

const STEP_MAX = 4;
const STEP_TITLES = ['單位基本資料', '房間組合', '燈制系列', '預算級別'];

function stepValid(step, answers) {
  if (step === 1) return answers.areaSqft >= 200 && answers.areaSqft <= 5000 && !!answers.propertyType;
  if (step === 2) return sumRooms(answers.rooms) >= 1;
  if (step === 3) {
    if (!answers.series) return false;
    return true;
  }
  if (step === 4) return !!answers.tier;
  return true;
}

function hashFor(state) {
  if (state.view === 'quote') return '#quote';
  return '#step-' + state.step;
}

function applyHash(state) {
  const hash = location.hash || '';
  if (hash === '#quote' || hash.indexOf('#quote') === 0) {
    state.view = 'quote';
    return;
  }
  const m = hash.match(/^#step-(\d)/);
  if (m) {
    state.view = 'wizard';
    state.step = clamp(Number(m[1]), 1, STEP_MAX);
  }
}

function setHash(state) {
  const next = location.pathname + location.search + hashFor(state);
  const cur = location.pathname + location.search + (location.hash || '');
  if (cur !== next) history.pushState({ sq: true }, '', next);
}

function shareUrl(state) {
  const rows = state.lines.filter(function (l) { return l.qty > 0; });
  const q = encodeURIComponent(encodeQuote(state.answers, rows));
  const origin = (typeof location !== 'undefined' && location.origin) ? location.origin : 'https://linko.shop';
  const path = (typeof location !== 'undefined' && location.pathname) ? location.pathname : '/pages/smart-quote';
  return origin + path + '?sq=' + q + '#quote';
}

function renderWizard(state, config) {
  const a = state.answers;
  const step = state.step;
  let body = '';
  if (step === 1) {
    body = '<label class="sq-field"><span>實用面積（呎）</span><div class="sq-area"><input type="number" min="200" max="5000" step="1" inputmode="numeric" value="' + a.areaSqft + '" data-sq="area-num" aria-label="實用面積（平方呎）"><span class="sq-area__unit">呎</span></div></label>' +
      '<p class="sq-q">單位類型</p><div class="sq-chips" role="radiogroup">' +
      PROPERTY_TYPES.map(function (p) {
        return '<button type="button" class="sq-chip' + (a.propertyType === p.id ? ' is-on' : '') + '" role="radio" aria-checked="' + (a.propertyType === p.id) + '" data-sq="property" data-id="' + p.id + '">' + p.label + '</button>';
      }).join('') + '</div>';
  } else if (step === 2) {
    body = '<p class="sq-q">房間組合</p><div class="sq-rooms">' + ROOM_KEYS.map(function (key) {
      const n = a.rooms[key] || 0;
      return '<div class="sq-room"><span>' + escapeHtml((config.roomLabels && config.roomLabels[key]) || key) + '</span><div class="sq-stepper"><button type="button" data-sq="room-" data-id="' + key + '" aria-label="減少">−</button><b>' + n + '</b><button type="button" data-sq="room+" data-id="' + key + '" aria-label="增加">+</button></div></div>';
    }).join('') + '</div>';
  } else if (step === 3) {
    const kindSeries = seriesByKind(config, a.smartMode);
    const current = kindSeries.filter(function (s) { return s.id === a.series; })[0] || kindSeries[0];
    const allowed = (current && current.colours) || [];
    body = '<p class="sq-q">燈制類型</p><div class="sq-cards two" role="radiogroup">' +
      '<button type="button" class="sq-card' + (!a.smartMode ? ' is-on' : '') + '" role="radio" aria-checked="' + (!a.smartMode) + '" data-sq="kind" data-id="standard"><b>普通燈制</b><small>機械掣、穩陣、易維修</small></button>' +
      '<button type="button" class="sq-card' + (a.smartMode ? ' is-on' : '') + '" role="radio" aria-checked="' + !!a.smartMode + '" data-sq="kind" data-id="smart"><b>智能燈制</b><small>場景、遙控、之後再升級</small></button></div>' +
      '<p class="sq-q">品牌系列</p><div class="sq-cards" role="radiogroup">' +
      kindSeries.map(function (s) {
        return '<button type="button" class="sq-card' + (a.series === s.id ? ' is-on' : '') + '" role="radio" aria-checked="' + (a.series === s.id) + '" data-sq="series" data-id="' + s.id + '"><b>' + escapeHtml(s.label) + '</b>' + (s.note ? '<small>' + escapeHtml(s.note) + '</small>' : '') + '</button>';
      }).join('') + '</div>';
    if (allowed.length) {
      body += '<p class="sq-q">顏色</p><div class="sq-swatches" role="radiogroup">' +
        (config.colours || []).filter(function (c) { return allowed.indexOf(c.id) !== -1; }).map(function (c) {
          return '<button type="button" class="sq-swatch' + (a.colour === c.id ? ' is-on' : '') + '" role="radio" aria-checked="' + (a.colour === c.id) + '" data-sq="colour" data-id="' + c.id + '"><i style="background:' + c.hex + '"></i>' + escapeHtml(c.label) + '</button>';
        }).join('') + '</div>';
    }
  } else {
    body = '<p class="sq-q">預算級別</p><div class="sq-cards" role="radiogroup">' + TIERS.map(function (t) {
      return '<button type="button" class="sq-card' + (a.tier === t.id ? ' is-on' : '') + '" role="radio" aria-checked="' + (a.tier === t.id) + '" data-sq="tier" data-id="' + t.id + '"><b>' + t.label + '</b><small>' + t.text + '</small></button>';
    }).join('') + '</div>';
  }
  return '<div class="sq-wiz"><div class="sq-panel"><div class="sq-progress" aria-hidden="true"><i style="width:' + (step / STEP_MAX * 100) + '%"></i></div><h2>' + STEP_TITLES[step - 1] + '</h2>' + body + '</div></div>' +
    (state.toast ? '<div class="sq-toast" role="status">' + escapeHtml(state.toast) + '</div>' : '');
}

function renderSteps(state) {
  const titles = STEP_TITLES;
  const step = state.step;
  return '<ol class="sq-steps">' + titles.map(function (t, i) {
    const n = i + 1;
    return '<li class="' + (step === n ? 'is-on' : '') + (n < step ? ' is-done' : '') + '"><button type="button" data-sq="goto" data-id="' + n + '">' + n + '. ' + t + '</button></li>';
  }).join('') + '</ol>';
}

function findCatalogProduct(catalog, handle, role) {
  const roles = (catalog && catalog.roles) || {};
  const keys = Object.keys(roles);
  if (role) keys.unshift(role);
  for (let i = 0; i < keys.length; i += 1) {
    const found = (roles[keys[i]] || []).filter(function (p) { return p.handle === handle; })[0];
    if (found) return found;
  }
  return null;
}

function variantColourId(variant, colours) {
  if (!variant) return '';
  if (variant.colour) return variant.colour;
  const list = colours || [];
  for (let i = 0; i < list.length; i += 1) {
    if (colourMatches(variant, list[i].id, list)) return list[i].id;
  }
  return '';
}

function variantGangNum(variant, product) {
  return variantGangOf(product || null, variant);
}

function applyLineVariant(line, product, variant) {
  line.handle = product.handle;
  line.title = product.title;
  if (product.image) line.image = product.image;
  line.variantId = variant.id;
  line.variantTitle = variant.title || '';
  line.price = variant.price || 0;
  line.compareAtPrice = variant.compareAtPrice || 0;
  line.available = variant.available !== false;
}

function pickLineVariant(product, colour, gang, colours) {
  return ((product && product.variants) || []).filter(function (v) {
    const c = variantColourId(v, colours);
    const g = variantGangOf(product, v);
    const cOk = !colour || c === colour || colourMatches(v, colour, colours);
    const gOk = !gang || g === gang;
    return cOk && gOk;
  })[0] || null;
}

function lineControls(line, catalog, config, answers) {
  const product = findCatalogProduct(catalog, line.handle, line.role) || { variants: [] };
  const coloursCfg = (config && config.colours) || [];
  const series = seriesRowOf(config, answers && answers.series) || seriesRowOf(config, inferSeries(product, config));
  const seriesColours = (series && series.colours) || [];
  const fromProduct = [];
  const gangs = [];
  (product.variants || []).forEach(function (v) {
    const c = variantColourId(v, coloursCfg);
    if (c && fromProduct.indexOf(c) === -1) fromProduct.push(c);
    const g = variantGangOf(product, v);
    if (g && gangs.indexOf(g) === -1) gangs.push(g);
  });
  const colourIds = seriesColours.length ? seriesColours : fromProduct;
  const current = (product.variants || []).filter(function (v) { return v.id === line.variantId; })[0] || {};
  const currentC = variantColourId(current, coloursCfg) || (answers && answers.colour) || '';
  const currentG = variantGangOf(product, current);
  let colourHtml = '';
  if (colourIds.length > 1) {
    colourHtml = '<label>顏色 <select data-sq="line-colour" data-vid="' + line.variantId + '">' + colourIds.map(function (c) {
      return '<option value="' + c + '"' + (c === currentC ? ' selected' : '') + '>' + escapeHtml(colourLabel(config, c)) + '</option>';
    }).join('') + '</select></label>';
  }
  let gangHtml = '';
  if (gangs.length > 1) {
    gangHtml = '<label>位數 <select data-sq="line-gang" data-vid="' + line.variantId + '">' + gangs.sort().map(function (g) {
      return '<option value="' + g + '"' + (g === currentG ? ' selected' : '') + '>' + g + '位</option>';
    }).join('') + '</select></label>';
  }
  return colourHtml + gangHtml;
}

function renderQuote(state, config, settings) {
  const groups = config.groups || {};
  const totals = totalsOf(state.lines);
  let html = '';
  if (state.shareBanner) html += '<div class="sq-banner">已載入分享報價</div>';
  const missing = (state.meta && state.meta.missingRoles) || [];
  if (missing.length) {
    html += '<div class="sq-banner">此系列未有：' + missing.map(function (role) {
      return ROLE_USAGE[role] || role;
    }).join('、') + '。可用 WhatsApp 問我哋配其他系列。</div>';
  }
  Object.keys(groups).forEach(function (name) {
    const rows = state.lines.filter(function (l) { return l.group === name && l.qty > 0; });
    if (!rows.length) return;
    let count = 0;
    let sub = 0;
    rows.forEach(function (l) { count += l.qty; sub += l.price * l.qty; });
    html += '<details class="sq-group" open><summary>' + escapeHtml(name) + ' · 建議 ' + count + ' 件 · 小計 ' + moneyHKD(sub) + '</summary>';
    rows.forEach(function (line) {
      html += '<article class="sq-line' + (line.available ? '' : ' is-oos') + '" data-vid="' + line.variantId + '">' +
        (line.image ? '<img src="' + escapeHtml(line.image) + '" alt="" width="72" height="72">' : '<div class="sq-ph"></div>') +
        '<div><h3>' + escapeHtml(line.title) + '</h3><div class="sq-meta">' + lineControls(line, state.catalog || { roles: {} }, config, state.answers) + '</div>' +
        '<p class="sq-reason">💡 ' + escapeHtml(line.reason || reasonForRole(line.role, state.answers, config, {})) + '</p>' +
        (line.available ? '' : '<p class="sq-oos">暫時缺貨 · 可預訂</p>') +
        '<div class="sq-row"><b>' + moneyHKD(line.price) + '</b><div class="sq-stepper"><button type="button" data-sq="qty-" data-vid="' + line.variantId + '">−</button><b>' + line.qty + '</b><button type="button" data-sq="qty+" data-vid="' + line.variantId + '">+</button></div><em>= ' + moneyHKD(line.price * line.qty) + '</em>' +
        '<button type="button" class="sq-link" data-sq="swap" data-vid="' + line.variantId + '">換產品</button>' +
        '<button type="button" class="sq-link" data-sq="del" data-vid="' + line.variantId + '">刪除</button></div></div></article>';
    });
    html += '</details>';
  });
  if (state.addons && state.addons.length) {
    html += '<div class="sq-addons"><h3>加購建議</h3>' + state.addons.map(function (line) {
      return '<article class="sq-addon"><div><b>' + escapeHtml(line.title) + '</b><p>' + escapeHtml(line.reason) + '</p><span>' + moneyHKD(line.price) + '</span></div><button type="button" data-sq="addon" data-vid="' + line.variantId + '">+ 加入報價</button></article>';
    }).join('') + '</div>';
  }
  const note = settings.showSales
    ? '<p class="sq-note">本建議參考 LINKO Shop 真實訂單及香港住宅裝修慣例，實際數量以現場勘察為準。此為系統初步估算，實際數量及價格以現場勘察及正式報價單為準。</p>'
    : '<p class="sq-note">此為系統初步估算，實際數量及價格以現場勘察及正式報價單為準。</p>';
  const save = totals.save > 0 ? '<p class="sq-save">原價 ' + moneyHKD(totals.compare) + '，慳 ' + moneyHKD(totals.save) + '</p>' : '';
  html += '<aside class="sq-sum"><p>產品小計</p><b>' + moneyHKD(totals.sub) + '</b>' + save + '<strong>總計 ' + moneyHKD(totals.grand) + '</strong>' +
    '<button type="button" class="sq-btn dark" data-sq="cart">一鍵加入購物車</button>' +
    '<button type="button" class="sq-btn wa" data-sq="wa">WhatsApp 傳送報價</button>' +
    '<button type="button" class="sq-btn ghost" data-sq="copy">複製報價連結</button>' +
    note + '</aside>';
  if (state.modal) {
    const role = state.modal.role;
    const cands = ((state.catalog && state.catalog.roles) || {})[role] || [];
    html += '<div class="sq-modal" data-sq="modal"><div class="sq-modal__box"><header><h3>換產品</h3><button type="button" data-sq="close-modal">關閉</button></header><div class="sq-modal__list">' +
      cands.map(function (p) {
        const v = p.variants[0];
        return '<button type="button" class="sq-pick" data-sq="pick" data-handle="' + escapeHtml(p.handle) + '" data-vid="' + v.id + '">' + (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="">' : '') + '<span><b>' + escapeHtml(p.title) + '</b><small>' + moneyHKD(v.price) + ' · ' + (p.series || '') + '</small></span></button>';
      }).join('') + '</div></div></div>';
  }
  if (state.toast) html += '<div class="sq-toast" role="status">' + escapeHtml(state.toast) + (state.undo ? ' <button type="button" data-sq="undo">復原</button>' : '') + '</div>';
  return html;
}

function bind(root, state, config, settings) {
  root.onclick = function (ev) {
    const btn = ev.target.closest('[data-sq]');
    if (!btn) return;
    const act = btn.getAttribute('data-sq');
    const id = btn.getAttribute('data-id');
    const vid = Number(btn.getAttribute('data-vid') || 0);
    if (act === 'property') state.answers.propertyType = id;
    if (act === 'need') {
      const i = state.answers.needs.indexOf(id);
      if (i === -1) state.answers.needs.push(id);
      else state.answers.needs.splice(i, 1);
    }
    if (act === 'kind') {
      state.answers.smartMode = id === 'smart';
      const first = seriesByKind(config, state.answers.smartMode)[0];
      if (first) state.answers.series = first.id;
      syncColourToSeries(state.answers, first);
    }
    if (act === 'series') {
      state.answers.series = id;
      const row = seriesRowOf(config, id);
      if (row) state.answers.smartMode = row.kind === 'smart';
      syncColourToSeries(state.answers, row);
    }
    if (act === 'colour') state.answers.colour = id;
    if (act === 'smart') {
      state.answers.smartMode = id === '1' || id === 'smart';
      const first = seriesByKind(config, state.answers.smartMode)[0];
      if (first && seriesRowOf(config, state.answers.series) && seriesRowOf(config, state.answers.series).kind !== (state.answers.smartMode ? 'smart' : 'standard')) {
        state.answers.series = first.id;
        syncColourToSeries(state.answers, first);
      }
    }
    if (act === 'tier') state.answers.tier = id;
    if (act === 'room+' || act === 'room-') {
      const next = (state.answers.rooms[id] || 0) + (act === 'room+' ? 1 : -1);
      state.answers.rooms[id] = Math.max(0, Math.min(8, next));
    }
    if (act === 'goto') {
      state.view = 'wizard';
      state.step = clamp(Number(id), 1, STEP_MAX);
      setHash(state);
    }
    if (act === 'qty+' || act === 'qty-' || act === 'del') {
      const line = state.lines.filter(function (l) { return l.variantId === vid; })[0];
      if (line) {
        const prev = Object.assign({}, line);
        line.qty = act === 'qty+' ? line.qty + 1 : line.qty - 1;
        if (act === 'del') line.qty = 0;
        if (line.qty <= 0) {
          state.lines = state.lines.filter(function (l) { return l !== line; });
          state.undo = prev;
          state.toast = '已移除一行';
          setTimeout(function () { if (state.toast === '已移除一行') { state.toast = ''; state.undo = null; paint(); } }, 5000);
        }
        track('sq_edit_line', { role: line.role, action: act === 'del' ? 'remove' : 'qty' });
      }
    }
    if (act === 'undo' && state.undo) {
      state.lines.push(state.undo);
      state.undo = null;
      state.toast = '';
    }
    if (act === 'swap') {
      const line = state.lines.filter(function (l) { return l.variantId === vid; })[0];
      if (line) state.modal = { role: line.role, vid: vid };
    }
    if (act === 'close-modal') state.modal = null;
    if (act === 'pick') {
      const line = state.lines.filter(function (l) { return l.variantId === state.modal.vid; })[0];
      const handle = btn.getAttribute('data-handle');
      const product = ((state.catalog.roles[state.modal.role] || []).filter(function (p) { return p.handle === handle; })[0]);
      const variant = product && product.variants.filter(function (v) { return v.id === Number(btn.getAttribute('data-vid')); })[0];
      if (line && product && variant) {
        line.handle = product.handle;
        line.title = product.title;
        line.image = product.image;
        line.variantId = variant.id;
        line.variantTitle = variant.title;
        line.price = variant.price;
        line.source = 'manual';
        track('sq_edit_line', { role: line.role, action: 'swap' });
      }
      state.modal = null;
    }
    if (act === 'addon') {
      const addon = state.addons.filter(function (l) { return l.variantId === vid; })[0];
      if (addon) {
        addon.qty = 1;
        addon.optional = false;
        state.lines.push(addon);
        state.addons = state.addons.filter(function (l) { return l !== addon; });
      }
    }
    if (act === 'line-colour' || act === 'line-gang') return;
    persist(state.answers);
    if (act === 'cart') addCart(state, settings);
    else if (act === 'wa') openWa(state, settings, config);
    else if (act === 'copy') copyShare(state);
    else {
      state.emit();
      paint();
    }
  };
  root.onchange = function (ev) {
    const t = ev.target;
    const act = t.getAttribute('data-sq');
    if (act === 'area-num') {
      const n = Number(t.value);
      if (!Number.isNaN(n)) state.answers.areaSqft = clamp(n, 200, 5000);
      persist(state.answers);
      return;
    }
    if (act === 'line-colour' || act === 'line-gang') {
      const vid = Number(t.getAttribute('data-vid'));
      const line = state.lines.filter(function (l) { return l.variantId === vid; })[0];
      const product = findCatalogProduct(state.catalog, line && line.handle, line && line.role);
      if (!line || !product) return;
      const current = (product.variants || []).filter(function (v) { return v.id === vid; })[0] || {};
      const colour = act === 'line-colour' ? t.value : variantColourId(current, config.colours);
      const gang = act === 'line-gang' ? Number(t.value) : variantGangOf(product, current);
      let next = pickLineVariant(product, colour, gang, config.colours);
      let nextProduct = product;
      if (!next) {
        const siblings = catalogProducts(line.role, state.catalog);
        for (let i = 0; i < siblings.length; i += 1) {
          const cand = siblings[i];
          if (!seriesMatches(cand, state.answers.series, config) && inferSeries(cand, config) !== inferSeries(product, config)) continue;
          const found = pickLineVariant(cand, colour, gang, config.colours);
          if (found) {
            next = found;
            nextProduct = cand;
            break;
          }
        }
      }
      if (next) {
        applyLineVariant(line, nextProduct, next);
        track('sq_edit_line', { role: line.role, action: 'variant' });
      } else {
        state.toast = '此顏色／位數暫無變體，已保留原選項';
      }
      paint();
    }
  };
  root.oninput = function (ev) {
    const t = ev.target;
    if (t && t.getAttribute('data-sq') === 'area-num') {
      const n = Number(t.value);
      if (!Number.isNaN(n)) state.answers.areaSqft = clamp(n, 200, 5000);
      persist(state.answers);
    }
  };
  function paint() {
    renderApp(root, state, config, settings);
  }
}

async function generate(state, config, catalog, insights, settings) {
  if (state.dataReady) {
    try { await state.dataReady; } catch (err) {}
  }
  config = state.config || config;
  catalog = state.catalog || catalog;
  insights = state.insights || insights;
  const result = recommendEngine(state.answers, config, catalog, insights, {
    addonLimit: settings.addonLimit,
    autoAddInstall: settings.autoInstall,
  });
  state.lines = result.lines;
  state.addons = result.addons;
  state.meta = result.meta;
  state.catalog = catalog;
  try {
    await hydratePrices(state.lines.concat(state.addons));
  } catch (err) {}
  state.view = 'quote';
  const tot = totalsOf(state.lines);
  track('sq_generate', {
    value: tot.grand / 100,
    items: state.lines.length,
    area: state.answers.areaSqft,
    series: state.answers.series,
    tier: state.answers.tier,
    smart_mode: state.answers.smartMode,
  });
  const token = encodeQuote(state.answers, state.lines);
  persistShareToken(token);
  const q = encodeURIComponent(token);
  history.replaceState({ sq: true }, '', location.pathname + '?sq=' + q + '#quote');
}

async function addCart(state, settings) {
  const items = state.lines.filter(function (l) { return l.qty > 0; }).map(function (l) {
    return {
      id: l.variantId,
      quantity: l.qty,
      properties: {
        _sq_quote: encodeQuote(state.answers, state.lines).slice(0, 40),
        _sq_role: l.role,
        '智能選購配置': seriesLabel(state.config || {}, state.answers.series) + ' · ' + colourLabel(state.config || {}, state.answers.colour),
      },
    };
  });
  try {
    if (settings.cartBehavior === 'replace') await fetch('/cart/clear.js', { method: 'POST' });
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
    if (!res.ok) {
      const failed = [];
      for (let i = 0; i < items.length; i += 1) {
        const one = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [items[i]] }),
        });
        if (!one.ok) failed.push(items[i].id);
      }
      if (failed.length) {
        state.lines.forEach(function (l) {
          if (failed.indexOf(l.variantId) !== -1) {
            l.available = false;
            l.reason = '此型號需要訂貨，已轉為 WhatsApp 查詢項目';
          }
        });
        state.toast = '部分型號未能入購物車，請用 WhatsApp 跟進缺貨項目';
        state.emit();
        renderApp(findSection(), state, state.config, settings);
        return;
      }
    }
    const tot = totalsOf(state.lines);
    track('add_to_cart', { value: tot.grand / 100, currency: 'HKD', items: items });
    track('sq_add_to_cart', { value: tot.grand / 100, currency: 'HKD' });
    location.href = '/cart';
  } catch (err) {
    state.toast = '加入購物車失敗，請 WhatsApp 我哋';
    renderApp(findSection(), state, state.config, settings);
  }
}

function openWa(state, settings, config) {
  const tot = totalsOf(state.lines);
  const url = shareUrl(state);
  const text = buildWhatsAppText(state.answers, state.lines, tot, url, settings.prefix, config);
  track('sq_whatsapp', { value: tot.grand / 100, items: state.lines.length });
  track('generate_lead', { value: tot.grand / 100 });
  window.open('https://wa.me/' + settings.whatsapp + '?text=' + encodeURIComponent(text), '_blank', 'noopener');
}

function copyShare(state) {
  copyText(shareUrl(state)).then(function () {
    state.toast = '已複製報價連結';
    renderApp(findSection(), state, state.config, readSettings(findSection()));
    track('sq_share');
  });
}

function renderApp(root, state, config, settings) {
  if (!root) return;
  const section = root.classList && root.classList.contains('linko-sq') ? root : (root.closest ? root.closest('.linko-sq') : root);
  if (!section) return;
  const main = section.querySelector('[data-sq-root]');
  const aside = section.querySelector('.linko-sq__aside');
  const nav = section.querySelector('.linko-sq__nav');
  const layout = section.querySelector('.linko-sq__layout');
  if (!main || !layout) return;
  root = section;
  if (state.error) {
    main.innerHTML = '<div class="sq-error"><p>暫時載入唔到，請 WhatsApp 我哋</p><a class="sq-btn wa" href="https://wa.me/' + settings.whatsapp + '">WhatsApp</a></div>';
    return;
  }
  if (state.view === 'quote') {
    layout.classList.add('is-quote');
    section.classList.add('is-quote');
    if (aside) aside.hidden = true;
    if (!state.lines.length && state.shareBanner) {
      main.innerHTML = '<div class="sq-wiz"><div class="sq-panel"><h2>正在載入報價單…</h2><p class="sq-note">請稍候，正在還原分享內容。</p></div></div>';
    } else {
      main.innerHTML = renderQuote(state, config, settings);
    }
    if (nav) {
      nav.hidden = false;
      nav.innerHTML = '<button type="button" data-sq-back>返回修改</button><button type="button" data-sq-next hidden></button>';
    }
  } else {
    layout.classList.remove('is-quote');
    section.classList.remove('is-quote');
    if (aside) {
      aside.hidden = false;
      aside.innerHTML = renderSteps(state);
    }
    main.innerHTML = renderWizard(state, config);
    if (nav) {
      nav.hidden = false;
      nav.innerHTML = '<button type="button" data-sq-back' + (state.step === 1 ? ' disabled' : '') + '>上一步</button><button type="button" class="is-primary" data-sq-next>' + (state.step === STEP_MAX ? '產生報價單' : '下一步') + '</button>';
    }
  }
  bind(section, state, config, settings);
  if (nav) {
    const back = nav.querySelector('[data-sq-back]');
    const next = nav.querySelector('[data-sq-next]');
    if (back) {
      back.onclick = function () {
        if (state.view === 'quote') {
          state.view = 'wizard';
          state.step = STEP_MAX;
        } else if (state.step > 1) state.step -= 1;
        setHash(state);
        renderApp(root, state, config, settings);
      };
    }
    if (next) {
      next.onclick = function () {
        if (state.view === 'quote') return;
        if (!stepValid(state.step, state.answers)) {
          state.toast = '請完成此步再繼續';
          renderApp(root, state, config, settings);
          return;
        }
        track('sq_step', { step: state.step, answers_snapshot: state.answers });
        if (state.step < STEP_MAX) {
          state.step += 1;
          setHash(state);
          renderApp(root, state, config, settings);
        } else {
          generate(state, config, state.catalog, state.insights, settings).then(function () {
            renderApp(root, state, config, settings);
          });
        }
      };
    }
  }
}

async function loadJson(url, fallback) {
  let last = fallback;
  for (let i = 0; i < 2; i += 1) {
    try {
      const res = await fetchTimeout(url, 15000);
      if (res && res.ok) return await res.json();
    } catch (err) {}
  }
  return last;
}

async function boot() {
  const root = findSection();
  if (!root) return;
  const settings = readSettings(root);
  const state = new QuoteState();
  const shareToken = readShareToken();
  const saved = restoreAnswers();
  if (saved && !shareToken) state.answers = saved;
  applyHash(state);
  if (shareToken) {
    try {
      const preview = decodeQuote(shareToken);
      if (preview && preview.a) {
        state.answers = Object.assign(defaultAnswers(), preview.a);
        persistShareToken(shareToken);
      }
      state.view = 'quote';
      state.shareBanner = true;
    } catch (err) {}
  }
  track('sq_start');
  const emptyCatalog = { roles: {} };
  let config = readInlineJson('linko-sq-config') || { series: [], colours: [], groups: {}, roomLabels: {} };
  state.config = config;
  state.insights = {};
  state.catalog = emptyCatalog;
  state.dataReady = Promise.all([
    loadJson(settings.configUrl, config),
    loadJson(settings.insightsUrl, {}),
    loadJson(settings.catalogUrl, emptyCatalog),
  ]).then(function (pack) {
    state.config = pack[0] || config;
    state.insights = pack[1] || {};
    state.catalog = pack[2] || emptyCatalog;
    return pack;
  });
  renderApp(root, state, config, settings);
  root.setAttribute('data-ready', '1');

  const pack = await state.dataReady;
  config = pack[0] || config;
  const insights = pack[1] || {};
  const catalog = pack[2] || emptyCatalog;

  if (shareToken) {
    let decoded = null;
    try {
      decoded = decodeQuote(shareToken);
    } catch (err) {
      decoded = null;
    }
    if (decoded && decoded.a) {
      applySharedQuote(state, decoded, catalog, config, insights, settings);
      persistShareToken(shareToken);
      try {
        await hydratePrices(state.lines.concat(state.addons));
      } catch (errHydrate) {}
      try {
        const q = encodeURIComponent(shareToken);
        const keep = location.search || ('?sq=' + q);
        history.replaceState({ sq: true }, '', location.pathname + keep + '#quote');
      } catch (err2) {}
    } else {
      state.toast = '分享連結未能開啟，請重新產生報價';
    }
  }
  window.addEventListener('popstate', function () {
    applyHash(state);
    renderApp(root, state, state.config, settings);
  });
  renderApp(root, state, config, settings);
}

globalThis.LinkoSmartQuote = {
  clamp: clamp,
  addQty: addQty,
  sumRooms: sumRooms,
  accumulateBaseline: accumulateBaseline,
  applyAreaFactor: applyAreaFactor,
  applySmartUpgrade: applySmartUpgrade,
  evalNeedExpr: evalNeedExpr,
  resolveNeedQty: resolveNeedQty,
  applyWholeUnit: applyWholeUnit,
  applyTier: applyTier,
  blendWithSales: blendWithSales,
  colourMatches: colourMatches,
  variantColourId: variantColourId,
  groupForRole: groupForRole,
  inferSeries: inferSeries,
  seriesMatches: seriesMatches,
  pickVariant: pickVariant,
  consolidateGangs: consolidateGangs,
  buildAddons: buildAddons,
  roomSummary: roomSummary,
  reasonForRole: reasonForRole,
  recommendEngine: recommendEngine,
  ROOM_KEYS: ROOM_KEYS,
  NEED_CHIPS: NEED_CHIPS,
  PROPERTY_TYPES: PROPERTY_TYPES,
  TIERS: TIERS,
  defaultAnswers: defaultAnswers,
  QuoteState: QuoteState,
  encodeQuote: encodeQuote,
  decodeQuote: decodeQuote,
  linesFromShare: linesFromShare,
  applySharedQuote: applySharedQuote,
  readShareToken: readShareToken,
  sanitizeShareToken: sanitizeShareToken,
  moneyHKD: moneyHKD,
  totalsOf: totalsOf,
  seriesLabel: seriesLabel,
  colourLabel: colourLabel,
  buildWhatsAppText: buildWhatsAppText,
};

if (isBrowser) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
